import { queryOptions } from '@tanstack/react-query'

import Client from '@/api/client'
import { getConfig } from '@/app/config'
import type { Salutation, User } from '@/features/auth'

const client = new Client(getConfig().API_ENDPOINT ?? '')

const userServiceShipUsersPath = '/api/user-service/api/ship-users'

type UsersSearchResult = User[]

export const getUsers = async (): Promise<User[]> => {
  return await client.get<UsersSearchResult>(`${userServiceShipUsersPath}?limit=0&offset=0`)
}

export const getUser = async (id: string): Promise<User> => {
  const user = await client.get<User>(`${userServiceShipUsersPath}/${id}`)
  if (!user) {
    throw new Error(`Could not find user with ID ${id}`)
  }
  return user
}

export type UpdateUserProps = {
  userId: string
  data: {
    salutation?: Salutation
    title?: string
    firstName: string
    lastName: string
    phoneLandline?: string
    phoneMobile?: string
    roles: string[]
    organizationId: string
  }
}

export const updateUser = async (props: UpdateUserProps): Promise<void> => {
  await client.put(`${userServiceShipUsersPath}/${props.userId}`, props.data)
}

export type DeleteUserProps = {
  userId: string
}

export const deleteUser = async (props: DeleteUserProps): Promise<void> => {
  await client.delete(`${userServiceShipUsersPath}/${props.userId}`)
}

export type SendInvitationProps = {
  userId: string
}

export const sendInvitation = async (props: SendInvitationProps): Promise<void> => {
  await client.post(`${userServiceShipUsersPath}/${props.userId}/send-invite`)
}

export type CreateUserProps = {
  data: {
    salutation?: Salutation
    title?: string
    firstName: string
    lastName: string
    phoneLandline?: string
    phoneMobile?: string
    email: string
    roles: string[]
    organizationId: string
    customerId: string
  }
}

export const createUser = async (props: CreateUserProps): Promise<void> => {
  await client.post(`${userServiceShipUsersPath}`, props.data)
}

export const usersQueryOptions = () =>
  queryOptions({
    queryKey: ['users'],
    queryFn: getUsers,
  })

export const userQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['users', id],
    queryFn: () => getUser(id),
    staleTime: 60 * 60 * 1000,
  })
